import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import { useMemo } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { FormField } from "@sol/forms";
import ImageUploadInput from "@sol/forms/inputs/ImageUploadInput";
import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";
import { Trash01 } from "@sol/uikit/core/icons/General/Trash01";
import { DatePicker } from "@sol/uikit/data-entry/DatePicker/DatePicker";
import { Input } from "@sol/uikit/data-entry/Input/Input";
import { Button } from "@sol/uikit/general/Button/Button";

import { FactorySelect } from "@/entities/factory";
import { LanguageSelect } from "@/entities/language";
import { LocationSelect } from "@/entities/location";

import { UnrestrictedFrameworkSelect } from "../components/UnrestrictedFrameworkSelect";

const classroomSchema = yup.object({
    title: yup.string().max(100).required().default(""),
    description: yup.string().max(300).required().default(""),
    language: yup.string().required(),
    location: yup.string(),
    factory: yup.string().required(),
    frameworks: yup.array().of(yup.string().required()).min(1).required().default([]),
    externalReferences: yup
        .array()
        .of(
            yup.object({
                value: yup.string().required(),
            }),
        )
        .max(3)
        .required()
        .default([]),
    dates: yup.array().of(yup.date()).length(2).default([undefined, undefined]),
    image: yup.object({ "@id": yup.string().required() }).nullable().default(null),
});

type ClassroomFormValues = yup.InferType<typeof classroomSchema>;

type Props = {
    className?: string;
    submitLabel?: string;
    loading?: boolean;
    onSubmit: (data: ClassroomFormValues) => void;
    defaultValues?: Partial<ClassroomFormValues>;
    onImageDelete?: () => Promise<void>;
};

export function ClassroomForm({ className, submitLabel, loading, onSubmit, defaultValues, onImageDelete }: Props) {
    const [t] = useTranslation();

    const schema = useMemo(() => classroomSchema.clone(), [t]);

    const methods = useForm<ClassroomFormValues>({
        mode: "all",
        resolver: yupResolver(schema),
        defaultValues: defaultValues ? classroomSchema.cast(defaultValues) : schema.getDefault(),
    });

    const {
        handleSubmit,
        formState: { isSubmitting, isValid },
    } = methods;

    const externalReferencesFieldArray = useFieldArray({
        control: methods.control, // control props comes from useForm (optional: if you are using FormProvider)
        name: "externalReferences", // unique name for your Field Array
    });

    const size = "middle" as const;

    return (
        <FormProvider {...methods}>
            <form className={clsx("flex flex-col", className)} onSubmit={handleSubmit(onSubmit)}>
                <FormField
                    className="self-start"
                    label={t("form.addNewClassroom.language.label")}
                    name="language"
                    required
                    render={({ field }) => <LanguageSelect size={size} {...field} />}
                />

                <FormField
                    label={t("form.addNewClassroom.title.label")}
                    name="title"
                    required
                    render={({ field }) => (
                        <Input
                            size={size}
                            maxLength={100}
                            showCount
                            placeholder={t("form.addNewClassroom.title.placeholder")}
                            {...field}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.description.label")}
                    name="description"
                    required
                    render={({ field }) => (
                        <Input.TextArea
                            size={size}
                            maxLength={300}
                            showCount
                            placeholder={t("form.addNewClassroom.description.placeholder")}
                            {...field}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.location.label")}
                    name="location"
                    required
                    render={({ field }) => <LocationSelect size={size} {...field} />}
                />

                <FormField
                    label={t("form.addNewClassroom.dates.label")}
                    name="dates"
                    required
                    render={({ field }) => (
                        <DatePicker.RangePicker
                            className="w-full"
                            size={size}
                            {...field}
                            placeholder={[
                                t("form.addNewClassroom.startDate.label"),
                                t("form.addNewClassroom.endDate.label"),
                            ]}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.frameworks.label")}
                    name="frameworks"
                    required
                    render={({ field }) => (
                        <UnrestrictedFrameworkSelect
                            mode="multiple"
                            size={size}
                            placeholder={t("form.addNewClassroom.frameworks.placeholder")}
                            {...field}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.factory.label")}
                    name="factory"
                    required
                    render={({ field }) => (
                        <FactorySelect
                            size={size}
                            placeholder={t("form.addNewClassroom.factory.placeholder")}
                            {...field}
                        />
                    )}
                />

                <FormField
                    label={t("form.addNewClassroom.externalReferences.label")}
                    name="externalReferences"
                    required
                    render={() => {
                        const MAX = 3;
                        return (
                            <div className="ant-input-textarea-show-count ant-input-outlined ant-input-affix-wrapper w-full rounded-ant-lg border-ant-secondary! p-ant pt-ant-md">
                                <div className="flex w-full flex-col">
                                    <div className="flex flex-col">
                                        {externalReferencesFieldArray.fields.map((field, index) => (
                                            <FormField
                                                key={field.id}
                                                label=""
                                                name={`externalReferences.${index}.value`}
                                                render={({ field }) => {
                                                    return (
                                                        <div className="flex items-center gap-ant-sm">
                                                            <Input
                                                                size={size}
                                                                placeholder={t(
                                                                    "form.addNewClassroom.externalReferences.placeholder",
                                                                )}
                                                                {...field}
                                                            />
                                                            <Button
                                                                onClick={() =>
                                                                    externalReferencesFieldArray.remove(index)
                                                                }
                                                                className="aspect-square"
                                                                size={size}
                                                                danger
                                                                icon={<Trash01 variant="antd" />}
                                                                title={t(
                                                                    "form.addNewClassroom.externalReferences.removeReference",
                                                                )}
                                                            ></Button>
                                                        </div>
                                                    );
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <Button
                                        disabled={MAX <= externalReferencesFieldArray.fields.length}
                                        block
                                        type="dashed"
                                        size={size}
                                        onClick={() => externalReferencesFieldArray.append({ value: "" })}
                                    >
                                        {t("form.addNewClassroom.externalReferences.addReference")}
                                    </Button>
                                </div>
                                <span className="ant-input-suffix">
                                    <span className="ant-input-data-count">
                                        {externalReferencesFieldArray.fields.length} / {MAX}
                                    </span>
                                </span>
                            </div>
                        );
                    }}
                />

                <FormField
                    label={t("form.addNewClassroom.image.label")}
                    name="image"
                    render={({ field }) => (
                        <ImageUploadInput className="aspect-video" {...field} onDelete={onImageDelete} />
                    )}
                />

                <div className="flex flex-row items-center justify-end gap-ant">
                    <Button
                        disabled={isSubmitting || loading || !isValid}
                        loading={isSubmitting || loading}
                        htmlType="submit"
                        type="primary"
                        data-cy="submit-classroom-create"
                        icon={<ArrowRight variant="antd" />}
                        iconPosition="end"
                    >
                        {t(submitLabel || "form.addNewClassroom.submit")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
}
